/**
 * Cloud reporter orchestration — init, upload, and finalize cloud runs.
 *
 * Extracted from reporter.ts to comply with Constitution Principle II (500-line limit).
 * Never throws — all errors result in graceful degradation.
 */

import { join, dirname } from 'node:path';
import type { CloudConfig, TestRunReport, Summary, TestArtifacts } from '@testrelic/core';
import { CloudClient } from './cloud-client.js';
import { buildUploadPayload, uploadBatchRun, initRealtimeRun, finalizeRun, deriveRunTestType } from './cloud-upload.js';
import { detectCloudFarm } from './cloud-farm-detect.js';
import { resolveSdkRunTypeFromEnv } from './run-type-env.js';
import type { UploadFailure, TestResultForUpload } from './cloud-upload.js';
import { uploadArtifacts } from './cloud-artifact-upload.js';
import type { ArtifactUploadRequest } from './cloud-artifact-upload.js';
import { writeToQueue } from './cloud-queue.js';
import { detectCI } from './ci-detector.js';
import { normalizeGitRemoteUrl, deriveNonGitProjectId } from './git-metadata.js';
import type { CloudFinalizeOutcome } from './cloud-receipt.js';
import { Budget, DEFAULT_UPLOAD_BUDGET_MS } from './cloud-deadline.js';

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function getEffectiveGitId(cloudClient: CloudClient): string {
  const gitMeta = cloudClient.getGitMetadata();
  const remoteUrl = gitMeta?.remoteUrl;
  if (remoteUrl) return normalizeGitRemoteUrl(remoteUrl);
  const projectName = cloudClient.getConfig()?.projectName;
  return projectName ?? deriveNonGitProjectId(process.cwd());
}

// ---------------------------------------------------------------------------
// Realtime Init
// ---------------------------------------------------------------------------

/**
 * Initialize a realtime run on the cloud during onBegin.
 * Returns the cloud run ID, or null if init fails or is not applicable.
 */
export async function initCloudRun(
  cloudClient: CloudClient,
  uploadStrategy: string | undefined,
  testRunId: string,
  startedAt: string,
  environment?: string,
): Promise<string | null> {
  if (!cloudClient.isCloudMode()) return null;
  if (uploadStrategy !== 'realtime' && uploadStrategy !== 'both') return null;

  const tokenValid = await cloudClient.ensureValidToken();
  if (!tokenValid) return null;

  const gitMeta = cloudClient.getGitMetadata();
  const ciMeta = detectCI();
  const cloudFarm = detectCloudFarm();
  const runType = resolveSdkRunTypeFromEnv();

  const initResult = await initRealtimeRun(
    cloudClient.getEndpoint(),
    cloudClient.getAccessToken()!,
    {
      runId: testRunId,
      repoGitId: getEffectiveGitId(cloudClient),
      branch: gitMeta?.branch ?? null,
      commit: gitMeta?.commitSha ?? null,
      commitMessage: gitMeta?.commitMessage ?? null,
      commitAuthor: gitMeta?.commitAuthor ?? null,
      startedAt,
      totalTests: null,
      ciProvider: ciMeta?.provider ?? null,
      ciRunUrl: ciMeta?.runUrl ?? null,
      environment: environment ?? (cloudFarm.cloudPlatform ?? null),
      cloudPlatform: cloudFarm.cloudPlatform ?? null,
      cloudBuildId: cloudFarm.cloudBuildId ?? null,
      cloudSessionId: cloudFarm.cloudSessionId ?? null,
      ...(runType ? { runType } : {}),
      // Playwright runs are browser-driven by default; finalize overwrites this
      // with the derived slug once the per-test types are known. Ensures a run
      // that crashes before finalize still carries a non-empty test type.
      testType: 'e2e',
    },
  );

  return initResult?.runId ?? null;
}

// ---------------------------------------------------------------------------
// Artifact Collection
// ---------------------------------------------------------------------------

export interface ArtifactEntry {
  readonly testId: string;
  readonly artifacts: TestArtifacts;
  readonly outputDir: string;
}

const DEFAULT_UPLOAD_ARTIFACTS = true;
const DEFAULT_ARTIFACT_MAX_SIZE_MB = 50;

/** Artifact key map plus the counts that go into the run's upload receipt. */
interface ArtifactUploadOutcome {
  readonly keyMap: Map<string, TestArtifacts>;
  /** Artifact files offered for upload. */
  readonly total: number;
  /** Artifact files the platform confirmed it stored. */
  readonly uploaded: number;
}

const NO_ARTIFACTS: ArtifactUploadOutcome = {
  keyMap: new Map<string, TestArtifacts>(),
  total: 0,
  uploaded: 0,
};

/**
 * Upload all binary artifacts and return a map from testId to updated TestArtifacts
 * with cloud storage keys filled in, alongside the offered/confirmed counts.
 *
 * `cloudConfig` may be null when the user authenticated via `testrelic login` (session-based
 * auth) without an explicit apiKey in the reporter config. In that case we fall back to
 * safe defaults so artifacts are still uploaded.
 */
async function uploadRunArtifacts(
  cloudClient: CloudClient,
  cloudConfig: CloudConfig | null | undefined,
  runId: string,
  artifactEntries: readonly ArtifactEntry[],
  budget: Budget,
): Promise<ArtifactUploadOutcome> {
  const result = new Map<string, TestArtifacts>();

  const shouldUpload = cloudConfig?.uploadArtifacts ?? DEFAULT_UPLOAD_ARTIFACTS;
  if (!shouldUpload) return NO_ARTIFACTS;

  const tokenValid = await cloudClient.ensureValidToken();
  if (!tokenValid) return NO_ARTIFACTS;

  const requests: ArtifactUploadRequest[] = [];
  const requestToEntry = new Map<string, { testId: string; field: 'screenshot' | 'video' | 'trace' }>();

  for (const entry of artifactEntries) {
    if (entry.artifacts.screenshot) {
      const absPath = join(entry.outputDir, entry.artifacts.screenshot);
      requests.push({ filePath: absPath, runId, testId: entry.testId, type: 'screenshot' });
      requestToEntry.set(absPath, { testId: entry.testId, field: 'screenshot' });
    }
    if (entry.artifacts.video) {
      const absPath = join(entry.outputDir, entry.artifacts.video);
      requests.push({ filePath: absPath, runId, testId: entry.testId, type: 'video' });
      requestToEntry.set(absPath, { testId: entry.testId, field: 'video' });
    }
    if (entry.artifacts.trace) {
      const absPath = join(entry.outputDir, entry.artifacts.trace);
      requests.push({ filePath: absPath, runId, testId: entry.testId, type: 'trace' });
      requestToEntry.set(absPath, { testId: entry.testId, field: 'trace' });
    }
  }

  if (requests.length === 0) return NO_ARTIFACTS;

  const maxSizeMb = cloudConfig?.artifactMaxSizeMb ?? DEFAULT_ARTIFACT_MAX_SIZE_MB;
  const uploadResults = await uploadArtifacts(
    cloudClient.getEndpoint(),
    cloudClient.getAccessToken()!,
    requests,
    maxSizeMb,
    budget,
  );

  // Build updated TestArtifacts per testId
  const keysByTest = new Map<
    string,
    { screenshotKey?: string; videoKey?: string; traceKey?: string }
  >();
  for (const [filePath, uploadResult] of uploadResults) {
    if (!uploadResult.success || !uploadResult.storageKey) continue;
    const meta = requestToEntry.get(filePath);
    if (!meta) continue;
    const existing = keysByTest.get(meta.testId) ?? {};
    if (meta.field === 'screenshot') existing.screenshotKey = uploadResult.storageKey;
    if (meta.field === 'video') existing.videoKey = uploadResult.storageKey;
    if (meta.field === 'trace') existing.traceKey = uploadResult.storageKey;
    keysByTest.set(meta.testId, existing);
  }

  // Merge with original artifacts
  for (const entry of artifactEntries) {
    const keys = keysByTest.get(entry.testId);
    result.set(entry.testId, {
      ...entry.artifacts,
      ...(keys?.screenshotKey ? { screenshotKey: keys.screenshotKey } : {}),
      ...(keys?.videoKey ? { videoKey: keys.videoKey } : {}),
      ...(keys?.traceKey ? { traceKey: keys.traceKey } : {}),
    });
  }

  const uploaded = Array.from(uploadResults.values()).filter((r) => r.success).length;
  if (uploaded > 0) {
    process.stderr.write(`✓ TestRelic: Uploaded ${uploaded} artifact(s) to cloud storage.\n`);
  }

  return { keyMap: result, total: requests.length, uploaded };
}

// ---------------------------------------------------------------------------
// Finalize & Upload
// ---------------------------------------------------------------------------

/**
 * Finalize realtime run, perform batch upload, and dispose the cloud client.
 * Queues failed uploads for later retry. Never throws.
 *
 * Returns what the platform actually did, so the reporter can write an honest
 * `cloudUpload` receipt into `summary.json` instead of leaving consumers to infer
 * "uploaded" from the locally generated `testRunId`.
 *
 * @param artifactEntries - collected test artifacts to upload before the batch payload.
 *   When provided, binary files are uploaded and storage keys injected into the report.
 * @param outputDir - root output directory for resolving relative artifact paths.
 */
export async function finalizeAndUpload(
  cloudClient: CloudClient,
  cloudConfig: CloudConfig | undefined | null,
  uploadStrategy: string | undefined,
  testRunId: string,
  cloudRunId: string | null,
  report: TestRunReport,
  completedAt: string,
  totalDuration: number,
  summary: Summary,
  artifactEntries?: readonly ArtifactEntry[],
  tests?: readonly TestResultForUpload[],
): Promise<CloudFinalizeOutcome> {
  const outcome = {
    realtimeFinalizeAttempted: false,
    realtimeFinalized: false,
    batchAttempted: false,
    batchAcknowledged: false,
    artifactsTotal: 0,
    artifactsUploaded: 0,
    failureReason: null as string | null,
  };

  // ⛔ ONE budget for the whole phase, started here.
  //
  // This function runs inside Playwright's `onEnd`, which the runner awaits
  // before the process may exit — so everything below is, in the tester's
  // experience, "the run hasn't finished". Before this, nothing bounded it:
  // each request used bare `fetch` (undici's 5-minute defaults) inside a
  // 3-attempt retry, so a wedged endpoint held the process for a quarter of an
  // hour per request family, and Ctrl+C could not cancel a request that had no
  // abort signal. Measured on a real run: 11m49s of "Reporter is still stuck".
  //
  // Expiry is not data loss: the local report is already written, the queue
  // holds what did not land, and the next run retries.
  const budget = new Budget(cloudConfig?.uploadBudgetMs ?? DEFAULT_UPLOAD_BUDGET_MS);
  /** Keep the FIRST failure — it is the one that explains the rest. */
  const noteFailure = (reason: string): void => {
    if (!outcome.failureReason) outcome.failureReason = reason;
  };

  try {
    // Finalize realtime run (with retry; queue on failure for later retry)
    if (cloudClient.isCloudMode() && cloudRunId && (uploadStrategy === 'realtime' || uploadStrategy === 'both')) {
      outcome.realtimeFinalizeAttempted = true;
      const tokenValid = await cloudClient.ensureValidToken();
      const gitMetaForFinalize = cloudClient.getGitMetadata();
      // Realtime /runs/init ran before any test, so the run-level test type is
      // only fully known now. Send the derived slug (Web / API / Mobile / Unit);
      // the api protocol + metadata ride along only when the run is all-API.
      const finalizeTypeSignal = deriveRunTestType(tests);
      const finalizePayload = {
        finishedAt: completedAt,
        duration: totalDuration,
        summary,
        ...(gitMetaForFinalize?.commitMessage ? { commitMessage: gitMetaForFinalize.commitMessage } : {}),
        testType: finalizeTypeSignal.testType,
        ...(finalizeTypeSignal.testType === 'api'
          ? { apiProtocol: finalizeTypeSignal.apiProtocol, metadata: finalizeTypeSignal.metadata }
          : {}),
      };
      if (tokenValid) {
        const finalized = await finalizeRun(
          cloudClient.getEndpoint(),
          cloudClient.getAccessToken()!,
          cloudRunId,
          finalizePayload,
        );
        outcome.realtimeFinalized = finalized;
        if (!finalized) {
          noteFailure('finalize_failed_after_retries');
          const queueDir = cloudConfig?.queueDirectory ?? '.testrelic/queue';
          writeToQueue(
            queueDir,
            testRunId,
            'finalize',
            'finalize_failed_after_retries',
            `${cloudClient.getEndpoint()}/runs/${cloudRunId}/finalize`,
            'POST',
            finalizePayload as unknown as Record<string, unknown>,
            { 'Content-Type': 'application/json' },
          );
        }
      } else {
        noteFailure(cloudClient.getFailureReason() ?? 'token_invalid');
        const queueDir = cloudConfig?.queueDirectory ?? '.testrelic/queue';
        writeToQueue(
          queueDir,
          testRunId,
          'finalize',
          cloudClient.getFailureReason() ?? 'token_invalid',
          `${cloudClient.getEndpoint()}/runs/${cloudRunId}/finalize`,
          'POST',
          finalizePayload as unknown as Record<string, unknown>,
          { 'Content-Type': 'application/json' },
        );
      }
    }

    // Upload binary artifacts before building the batch payload.
    // cloudConfig may be null when the user authenticated via `testrelic login` — fall back
    // to defaults inside uploadRunArtifacts so artifacts still reach cloud storage.
    let artifactKeyMap = new Map<string, TestArtifacts>();
    if (cloudClient.isCloudMode() && artifactEntries && artifactEntries.length > 0) {
      const artifactOutcome = await uploadRunArtifacts(cloudClient, cloudConfig, testRunId, artifactEntries, budget);
      artifactKeyMap = artifactOutcome.keyMap;
      outcome.artifactsTotal = artifactOutcome.total;
      outcome.artifactsUploaded = artifactOutcome.uploaded;
    }

    // Batch upload (default, or 'both' mode)
    if (cloudClient.isCloudMode() && (!uploadStrategy || uploadStrategy === 'batch' || uploadStrategy === 'both')) {
      outcome.batchAttempted = true;
      const tokenValid = await cloudClient.ensureValidToken();
      if (tokenValid) {
        const batchGit = cloudClient.getGitMetadata();
        const batchCi = detectCI();
        const batchGitId = getEffectiveGitId(cloudClient);

        // Inject artifact storage keys into the report timeline before uploading
        const enrichedReport = injectArtifactKeys(report, artifactKeyMap);
        const payload = buildUploadPayload(enrichedReport, batchGitId, batchGit, batchCi, tests, detectCloudFarm());

        const result = await uploadBatchRun(
          cloudClient.getEndpoint(),
          cloudClient.getAccessToken()!,
          payload,
          async () => {
            const refreshed = await cloudClient.ensureValidToken();
            return refreshed ? cloudClient.getAccessToken() : null;
          },
          cloudConfig?.timeout,
          budget,
        );

        outcome.batchAcknowledged = result.success;
        if (!result.success) {
          const failure = result as UploadFailure;
          noteFailure(failure.reason);
          const queueDir = cloudConfig?.queueDirectory ?? '.testrelic/queue';
          writeToQueue(queueDir, testRunId, 'batch', failure.reason, failure.targetEndpoint, failure.method, failure.payload, { 'Content-Type': 'application/json' });
        }
      } else {
        // Token invalid — queue the payload
        noteFailure(cloudClient.getFailureReason() ?? 'token_invalid');
        const queueDir = cloudConfig?.queueDirectory ?? '.testrelic/queue';
        const queueGit = cloudClient.getGitMetadata();
        const queueCi = detectCI();
        const queueGitId = getEffectiveGitId(cloudClient);
        const queuePayload = buildUploadPayload(report, queueGitId, queueGit, queueCi, undefined, detectCloudFarm());
        writeToQueue(
          queueDir,
          testRunId,
          'batch',
          cloudClient.getFailureReason() ?? 'token_invalid',
          `${cloudClient.getEndpoint()}/runs`,
          'POST',
          queuePayload as unknown as Record<string, unknown>,
          { 'Content-Type': 'application/json' },
        );
      }
    }

    // Dispose cloud client (clean up timers, flush)
    await cloudClient.dispose();
  } catch (err) {
    // Cloud errors never prevent local report writing — but they must still be
    // reported, or the receipt would claim a clean upload after an exception.
    noteFailure(err instanceof Error ? err.message : 'unexpected_upload_error');
    try { await cloudClient.dispose(); } catch { /* ignore */ }
  }
  return outcome;
}

/**
 * Inject cloud storage keys into timeline steps that have matching test artifacts.
 * Returns a shallow copy of the report with enriched timeline entries.
 */
function injectArtifactKeys(
  report: TestRunReport,
  keyMap: Map<string, TestArtifacts>,
): TestRunReport {
  if (keyMap.size === 0) return report;

  const enrichedTimeline = report.timeline.map((entry) => {
    const testId = (entry as Record<string, unknown>).testId as string | undefined;
    if (!testId) return entry;

    const keys = keyMap.get(testId);
    if (!keys) return entry;

    return {
      ...entry,
      ...(keys.screenshotKey ? { screenshotKey: keys.screenshotKey } : {}),
      ...(keys.videoKey ? { videoKey: keys.videoKey } : {}),
    };
  });

  return { ...report, timeline: enrichedTimeline };
}
